import React, { useEffect, useRef, useState } from 'react';
import { Plus, Phone, Mail, Flame, Snowflake, Thermometer, LayoutGrid, List, X, Car, Paperclip, FileText, Trash2, StickyNote, Headphones, UserCheck, Archive } from 'lucide-react';
import api, { API } from '../lib/api';
import { leadStages, formatCurrency } from '../mock';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { LeadWorkspace } from '../components/LeadWorkspace';
import { useDeepLink } from '../hooks/useDeepLink';
import { ArchivedLeads } from '../components/ArchivedLeads';

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors';

const ContractSignedDialog = ({ lead, open, onClose, onDone }) => {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [vq, setVq] = useState('');
  const [financeType, setFinanceType] = useState('Finance');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      api.get('/vehicles').then(({ data }) => setVehicles(data.filter((v) => v.status !== 'Vendido' && v.status !== 'Entregue'))).catch(() => {});
      setVehicleId(lead?.vehicle_id || ''); setVq('');
    }
  }, [open, lead]);
  const selected = vehicles.find((v) => v.id === vehicleId);
  const results = (() => {
    const terms = vq.toLowerCase().split(/\s+/).filter(Boolean);
    const hay = (v) => `${v.ano || ''} ${v.marca || ''} ${v.modelo || ''} ${v.trim || ''} ${v.cor || ''} ${v.vin || ''} ${v.placa || ''} ${v.status || ''}`.toLowerCase();
    return vehicles.filter((v) => terms.every((t) => hay(v).includes(t))).slice(0, 40);
  })();
  const confirm = async () => {
    if (!vehicleId) { toast({ title: 'Selecione o veículo vendido' }); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/deals/contract-signed', { lead_id: lead.id, vehicle_id: vehicleId, finance_type: financeType });
      toast({ title: '🎉 NEW SALE', description: `Deal ${data.id} criado — enviado ao F&I e removido do estoque disponível.` });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Contract Signed</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Cliente: <span className="text-foreground font-semibold">{lead?.nome}</span>. Isso registra a venda, cria o Deal, envia ao F&I e remove o veículo do estoque disponível.</p>
        <div className="space-y-4 py-1">
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Veículo vendido * <span className="text-muted-foreground/60">({vehicles.length} no estoque)</span></label>
            {selected ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-400/40 bg-emerald-400/[0.07] px-3 py-2.5" data-testid="cs-vehicle-selected">
                <span className="text-sm font-semibold">{selected.ano} {selected.marca} {selected.modelo} · #{selected.placa} · ${Number(selected.preco).toLocaleString('en-US')}</span>
                <button onClick={() => setVehicleId('')} className="text-[11px] font-bold text-muted-foreground hover:text-foreground shrink-0">Trocar</button>
              </div>
            ) : (
              <>
                <input data-testid="cs-vehicle-search" autoFocus className={inputCls} placeholder="Digite qualquer dado: corolla, 2021, prata, VIN, stock..." value={vq} onChange={(e) => setVq(e.target.value)} />
                <div className="mt-1.5 max-h-[220px] overflow-y-auto rounded-lg border border-border bg-popover divide-y divide-border/50" data-testid="cs-vehicle-results">
                  {results.map((v) => (
                    <button key={v.id} data-testid={`cs-pick-${v.id}`} onClick={() => setVehicleId(v.id)} className="w-full text-left text-[12px] px-3 py-2 hover:bg-muted/50 transition-colors flex items-center justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{v.ano} {v.marca} {v.modelo}{v.trim ? ` ${v.trim}` : ''}</span>
                        <span className="block truncate text-[10.5px] text-muted-foreground/70">#{v.placa || '—'}{v.cor && v.cor !== '-' ? ` · ${v.cor}` : ''}{v.km ? ` · ${Number(v.km).toLocaleString('en-US')} mi` : ''}{v.vin ? ` · ${v.vin.slice(-8)}` : ''} · {v.status}</span>
                      </span>
                      <span className="font-semibold tabular-nums shrink-0">${Number(v.preco).toLocaleString('en-US')}</span>
                    </button>
                  ))}
                  {results.length === 0 && <p className="text-[12px] text-muted-foreground/70 px-3 py-3">Nenhum veículo do estoque bate com "{vq}".</p>}
                </div>
              </>
            )}
          </div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Tipo</label>
            <select data-testid="cs-finance-type" className={inputCls} value={financeType} onChange={(e) => setFinanceType(e.target.value)}>
              {['Cash', 'Finance', 'Lease'].map((t) => <option key={t} className="bg-popover">{t}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
          <button data-testid="cs-confirm" onClick={confirm} disabled={saving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-60">{saving ? 'Registrando...' : 'Confirmar Venda'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const LostDialog = ({ lead, open, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const confirm = async () => {
    if (!reason.trim()) { toast({ title: 'Justificativa obrigatória' }); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/lost`, { reason: reason.trim() });
      toast({ title: 'Lead marcado como Perdido', description: 'A justificativa foi registrada no card.' });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase text-red-400">Marcar como Perdido?</DialogTitle></DialogHeader>
        <p className="text-sm text-foreground/65 -mt-1">Tem certeza que o lead <span className="text-foreground font-semibold">{lead?.nome}</span> foi perdido? Essa ação registra a perda no dashboard.</p>
        <div>
          <label className="text-xs text-muted-foreground mb-1.5 block">Justificativa * (entra como note no card)</label>
          <textarea data-testid="lost-reason-input" rows={3} className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: comprou em outra loja, sem crédito aprovado, parou de responder..." />
        </div>
        <DialogFooter>
          <button data-testid="lost-cancel" onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
          <button data-testid="lost-confirm" onClick={confirm} disabled={saving || !reason.trim()} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50">Confirmar Perda</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const fmtDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

const tempIcon = {
  'Quente': <Flame className="w-3.5 h-3.5 text-orange-500" />,
  'Morno': <Thermometer className="w-3.5 h-3.5 text-amber-400" />,
  'Frio': <Snowflake className="w-3.5 h-3.5 text-sky-400" />,
};
const origemColor = {
  'Instagram': 'text-pink-400 bg-pink-400/10',
  'Site': 'text-sky-400 bg-sky-400/10',
  'WhatsApp': 'text-emerald-400 bg-emerald-400/10',
  'Indicação': 'text-purple-400 bg-purple-400/10',
  'Facebook': 'text-blue-400 bg-blue-400/10',
  'Capital One': 'text-red-400 bg-red-400/10',
  'CarGurus': 'text-teal-400 bg-teal-400/10',
};

const LEAD_ORIGENS = ['Loja física', 'Site', 'Instagram', 'Facebook', 'WhatsApp', 'Indicação', 'Telefone', 'CarGurus', 'Capital One', 'Landing Page', 'Outro'];

const STAGE_LIMIT_MIN = { 'Novo': 15 };
const DEFAULT_LIMIT_MIN = 24 * 60;
const NO_SLA_STAGES = ['Contract Signed', 'Perdido', 'Fechado'];

const idleInfo = (lead) => {
  if (NO_SLA_STAGES.includes(lead.etapa)) return null;
  const ref = lead.last_interaction_at || lead.stage_entered_at;
  if (!ref) return null;
  const mins = Math.floor((Date.now() - new Date(ref).getTime()) / 60000);
  if (isNaN(mins) || mins < 0) return null;
  const limit = STAGE_LIMIT_MIN[lead.etapa] || DEFAULT_LIMIT_MIN;
  return { mins, limit, overdue: mins >= limit };
};

export const fmtIdle = (m) => m < 60 ? `${m}min` : m < 1440 ? `${Math.floor(m / 60)}h ${m % 60}min` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`;

const LeadCard = ({ lead, onOpen, onDelete, onTransfer, onArchive }) => {
  const idle = idleInfo(lead);
  return (
  <div draggable data-testid={`crm-card-${lead.id}`} onDragStart={(e) => e.dataTransfer.setData('leadId', lead.id)} onClick={() => onOpen(lead.id)} className={`group relative bg-card border border-border rounded-[3px] p-2.5 cursor-grab active:cursor-grabbing transition-colors hover:border-primary/40 ${idle?.overdue ? 'border-l-2 border-l-[hsl(var(--bad))]' : ''}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="text-[13px] font-medium leading-tight">{lead.nome}</p>
      {tempIcon[lead.temperatura]}
    </div>
    <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{lead.vehicle_desc || lead.interesse}</p>
    {lead.lead_value > 0 && (
      <p className="num text-[12px] font-semibold mt-0.5" data-testid={`crm-card-price-${lead.id}`}>
        {formatCurrency(lead.lead_value)}
        {lead.value_source === 'manual' && <span className="ml-1 text-[9px] font-bold uppercase text-primary">negociado</span>}
        {lead.meta_value_sent_at && <span className="ml-1 text-[9px] font-bold uppercase t-ok">meta ✓</span>}
      </p>
    )}
    {lead.etapa === 'Perdido' && lead.lost_reason && <p className="text-[10px] t-bad mt-1 line-clamp-2" data-testid={`lost-reason-${lead.id}`}>{lead.lost_reason}</p>}
    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
      <span className="ent-tag">{lead.origem}</span>
      {lead.bdc_lead_id && <span className="ent-tag" data-testid={`crm-bdc-${lead.id}`}><Headphones className="w-2.5 h-2.5" /> BDC</span>}
      <span className="text-[10px] t-label ml-auto">{(lead.vendedor || '').split(' ')[0]}</span>
    </div>
    {idle && (
      <p className={`num mt-1.5 text-[10.5px] ${idle.overdue ? 't-bad font-semibold' : 't-label'}`} data-testid={`crm-idle-${lead.id}`}>
        {fmtIdle(idle.mins)} sem interação{idle.overdue ? ' · follow-up' : ''}
      </p>
    )}
    <div className="flex items-center gap-2.5 mt-2 pt-2 border-t border-border/70 text-muted-foreground">
      <a href={`tel:${lead.telefone}`} onClick={(e) => e.stopPropagation()} className="hover:text-foreground transition-colors"><Phone className="w-3 h-3" /></a>
      <a href={`mailto:${lead.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-foreground transition-colors"><Mail className="w-3 h-3" /></a>
      <span className="text-[10.5px] num ml-auto t-label">{lead.telefone}</span>
      {onArchive && <button data-testid={`crm-archive-${lead.id}`} title="Arquivar lead" onClick={(e) => { e.stopPropagation(); onArchive(lead); }} className="opacity-0 group-hover:opacity-100 hover:text-primary transition-all"><Archive className="w-3 h-3" /></button>}
      {onTransfer && <button data-testid={`crm-transfer-${lead.id}`} title="Transferir para outro vendedor" onClick={(e) => { e.stopPropagation(); onTransfer(lead); }} className="opacity-0 group-hover:opacity-100 hover:text-primary transition-all"><UserCheck className="w-3 h-3" /></button>}
      {onDelete && <button data-testid={`crm-delete-${lead.id}`} title="Excluir lead" onClick={(e) => { e.stopPropagation(); onDelete(lead); }} className="opacity-0 group-hover:opacity-100 hover:text-[hsl(var(--bad))] transition-all"><Trash2 className="w-3 h-3" /></button>}
    </div>
  </div>
  );
};

const TransferDialog = ({ lead, open, onClose, onDone }) => {
  const [sps, setSps] = useState([]);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget(''); setReason('');
    api.get('/leads/salespeople').then(({ data }) => setSps(data)).catch(() => {});
  }, [open]);

  const confirm = async () => {
    if (!target) { toast({ title: 'Escolha o vendedor' }); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/leads/${lead.id}/transfer`, { to_user_id: target, reason });
      toast({ title: 'Cliente transferido', description: `${lead.nome}: ${data.from} → ${data.vendedor}` });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: 'Não foi possível transferir', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  const options = sps.filter((s) => s.name !== lead?.vendedor);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md" data-testid="transfer-dialog">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Transferir cliente</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          <span className="text-foreground font-semibold">{lead?.nome}</span> está com <span className="text-foreground font-semibold">{lead?.vendedor || 'ninguém'}</span>. Só aparecem usuários com cargo de Vendedor.
        </p>
        <div className="space-y-4 py-1">
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Novo vendedor *</label>
            <select data-testid="transfer-target" className={inputCls} value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="" className="bg-popover">Selecione...</option>
              {options.map((s) => <option key={s.user_id} value={s.user_id} className="bg-popover">{s.name}</option>)}
            </select>
            {!options.length && <p className="text-[11px] t-warn mt-1.5">Nenhum outro usuário com cargo de Vendedor cadastrado.</p>}
          </div>
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Motivo (opcional)</label>
            <input data-testid="transfer-reason" className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: vendedor de férias" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
          <button data-testid="transfer-confirm" onClick={confirm} disabled={saving || !target} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Transferindo...' : 'Confirmar transferência'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Crm = () => {
  const { user } = useAuth();
  const isSalesperson = user?.role === 'Salesperson';
  const canDelete = ['Owner', 'Administrator'].includes(user?.role) || (user?.role || '').endsWith('Manager');
  const [leads, setLeads] = useState([]);
  const [transferLead, setTransferLead] = useState(null);
  const [view, setView] = useState('kanban');
  const [csLead, setCsLead] = useState(null);
  const [lostLead, setLostLead] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [dragCol, setDragCol] = useState(null);
  const [, setTick] = useState(0);
  const [newOpen, setNewOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const emptyLead = { nome: '', telefone: '', email: '', interesse: '', origem: 'Loja física', etapa: 'Novo', vendedor: '', temperatura: 'Morno' };
  const [nForm, setNForm] = useState(emptyLead);
  const [nSaving, setNSaving] = useState(false);
  const [sellerFilter, setSellerFilter] = useState('');
  const [mobileStage, setMobileStage] = useState(leadStages[0]);
  const [archOpen, setArchOpen] = useState(false);
  // Owner, Admin e qualquer gerente podem filtrar a carteira por vendedor.
  const canFilterSellers = ['Owner', 'Administrator'].includes(user?.role) || (user?.role || '').endsWith('Manager');

  useDeepLink('lead', setDetailId);

  const load = async () => { try { const r = await api.get('/leads'); setLeads(r.data); } catch (e) {} };
  useEffect(() => { load(); const iv = setInterval(load, 45000); return () => clearInterval(iv); }, []);
  useEffect(() => { const iv = setInterval(() => setTick((t) => t + 1), 30000); return () => clearInterval(iv); }, []);
  useEffect(() => { api.get('/users').then((r) => setTeam(r.data.filter((x) => ['Salesperson', 'Sales Manager'].includes(x.role)))).catch(() => {}); }, []);

  const createNewLead = async () => {
    if (!nForm.nome || (!nForm.telefone && !nForm.email)) { toast({ title: 'Dados obrigatórios', description: 'Informe nome e um contato (telefone ou e-mail).' }); return; }
    setNSaving(true);
    try {
      await api.post('/leads', { ...nForm, vendedor: isSalesperson ? user.name : nForm.vendedor });
      toast({ title: 'Lead criado', description: nForm.nome });
      setNewOpen(false); setNForm(emptyLead); load();
    } catch (e) { toast({ title: 'Erro ao criar lead', description: e.response?.data?.detail || '' }); }
    finally { setNSaving(false); }
  };

  const moveLead = async (id, stage) => {
    const lead = leads.find((l) => l.id === id);
    if (!lead || lead.etapa === stage) return;
    if (stage === 'Contract Signed') { setCsLead(lead); return; }
    if (stage === 'Perdido') { setLostLead(lead); return; }
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, etapa: stage } : l)));
    try {
      await api.put(`/leads/${id}`, { ...lead, etapa: stage });
      toast({ title: 'Lead movido', description: `${lead.nome} → ${stage}` });
    } catch (e) {
      toast({ title: 'Erro', description: 'Não foi possível mover o lead.' });
      load();
    }
  };

  const handleDrop = (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('leadId');
    if (id) moveLead(id, stage);
  };

  const archiveLead = async (lead) => {
    try {
      await api.post(`/leads/${lead.id}/archive`);
      toast({ title: 'Lead arquivado', description: `${lead.nome} saiu do funil (consulte em Arquivados)` });
      load();
    } catch (e) { toast({ title: 'Erro ao arquivar', description: e.response?.data?.detail || '' }); }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Excluir o lead "${lead.nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast({ title: 'Lead excluído', description: lead.nome });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast({ title: 'Erro ao excluir', description: typeof d === 'string' ? d : 'Tente novamente.' });
    }
  };

  const sellerCounts = leads.reduce((acc, l) => { const k = l.vendedor || 'Sem vendedor'; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const visibleLeads = sellerFilter ? leads.filter((l) => (l.vendedor || 'Sem vendedor') === sellerFilter) : leads;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <button data-testid="crm-view-kanban" onClick={() => setView('kanban')} className={`ent-btn ent-btn-sm ${view === 'kanban' ? 'ent-btn-primary' : ''}`}><LayoutGrid className="w-3.5 h-3.5" /> Funil</button>
          <button data-testid="crm-view-lista" onClick={() => setView('lista')} className={`ent-btn ent-btn-sm ${view === 'lista' ? 'ent-btn-primary' : ''}`}><List className="w-3.5 h-3.5" /> Lista</button>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button data-testid="crm-archived-btn" onClick={() => setArchOpen(true)} className="ent-btn"><Archive className="w-3.5 h-3.5" /> Arquivados</button>
          <button data-testid="crm-new-lead-btn" onClick={() => setNewOpen(true)} className="ent-btn ent-btn-primary"><Plus className="w-3.5 h-3.5" /> Novo lead</button>
        </div>
      </div>

      {canFilterSellers && (
        <div className="flex items-center gap-1.5 flex-wrap border-y border-border py-2.5" data-testid="crm-seller-bar">
          <span className="ent-label mr-1">Visão por vendedor</span>
          <button data-testid="crm-seller-all" onClick={() => setSellerFilter('')} className={`ent-btn ent-btn-sm ${!sellerFilter ? 'ent-btn-primary' : ''}`}>
            Todos <span className="ent-pill ml-1.5">{leads.length}</span>
          </button>
          {Object.entries(sellerCounts).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
            <button key={name} data-testid={`crm-seller-${name.replace(/\s+/g, '-')}`} onClick={() => setSellerFilter(name)}
              className={`ent-btn ent-btn-sm ${sellerFilter === name ? 'ent-btn-primary' : ''}`}>
              {name} <span className="ent-pill ml-1.5">{count}</span>
            </button>
          ))}
          {sellerFilter && <span className="text-[11px] t-label ml-2">Mostrando apenas os clientes de <b className="text-foreground">{sellerFilter}</b></span>}
        </div>
      )}

      {view === 'kanban' ? (
        <>
        <div className="md:hidden mob-chiprow flex gap-1.5 pb-1" data-testid="crm-mobile-stages">
          {leadStages.map((stage) => {
            const n = visibleLeads.filter((l) => l.etapa === stage || (stage === 'Perdido' && l.etapa === 'Fechado')).length;
            return (
              <button key={stage} data-testid={`crm-mstage-${stage}`} onClick={() => setMobileStage(stage)}
                className={`ent-btn ent-btn-sm ${mobileStage === stage ? 'ent-btn-primary' : ''}`}>
                {stage} <span className="ent-pill ml-1.5">{n}</span>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {leadStages.map((stage) => {
            const stageLeads = visibleLeads.filter((l) => l.etapa === stage || (stage === 'Perdido' && l.etapa === 'Fechado'));
            const isLost = stage === 'Perdido';
            return (
              <div key={stage} data-testid={`crm-col-${stage}`} onDragOver={(e) => { e.preventDefault(); if (dragCol !== stage) setDragCol(stage); }} onDragLeave={() => setDragCol((c) => (c === stage ? null : c))} onDrop={(e) => { handleDrop(e, stage); setDragCol(null); }} className={`rounded-[3px] border p-2 md:min-h-[220px] transition-colors ${stage === mobileStage ? '' : 'hidden md:block'} ${dragCol === stage ? 'kanban-active' : 'border-border bg-card/40'}`}>
                <div className="mb-2.5 pb-2 border-b border-border">
                  <div className="flex items-center gap-2">
                    <span className={`ent-dot ${isLost ? 't-bad' : 'text-primary'}`} />
                    <h3 className={`text-[12px] font-semibold ${isLost ? 't-bad' : 'text-foreground'}`}>{stage}</h3>
                    <span className="ent-pill ml-auto" data-testid={`crm-col-count-${stage}`}>{stageLeads.length}</span>
                  </div>
                  <p className="num mt-1 text-[11px] t-label" data-testid={`crm-col-value-${stage}`}>{formatCurrency(stageLeads.reduce((s, l) => s + (l.lead_value || l.vehicle_price || 0), 0))}</p>
                </div>
                <div className="space-y-2">{stageLeads.map((l) => <LeadCard key={l.id} lead={l} onOpen={setDetailId} onDelete={canDelete ? deleteLead : null} onTransfer={setTransferLead} onArchive={archiveLead} />)}
                  {stageLeads.length === 0 && <p className="text-[11px] t-label text-center py-5">Vazio</p>}
                </div>
              </div>
            );
          })}
        </div>
        </>
      ) : (
        <div className="rounded-xl border border-border/70 bg-card p-3 sm:p-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground text-xs tracking-wider border-b border-border/70">
                <th className="pb-3 font-semibold">NOME</th>
                <th className="pb-3 font-semibold hidden md:table-cell">INTERESSE</th>
                <th className="pb-3 font-semibold hidden lg:table-cell">CONTATO</th>
                <th className="pb-3 font-semibold">ORIGEM</th>
                <th className="pb-3 font-semibold hidden sm:table-cell">VENDEDOR</th>
                <th className="pb-3 font-semibold">ETAPA</th>
                <th className="pb-3 font-semibold text-right">TEMP.</th>
                <th className="pb-3 font-semibold text-right"></th>
              </tr>
            </thead>
            <tbody>
              {visibleLeads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-border/60 row-hover cursor-pointer" onClick={() => setDetailId(l.id)}>
                  <td className="py-3.5 font-medium">{l.nome}</td>
                  <td className="py-3.5 text-foreground/75 hidden md:table-cell">{l.vehicle_desc || l.interesse}{l.lead_value > 0 && <span className="num ml-2 text-[11px] font-semibold text-foreground">{formatCurrency(l.lead_value)}</span>}</td>
                  <td className="py-3.5 text-muted-foreground hidden lg:table-cell">{l.telefone}</td>
                  <td className="py-3.5"><span className={`text-[11px] px-2 py-1 rounded-full font-medium ${origemColor[l.origem] || 'text-muted-foreground bg-muted/40'}`}>{l.origem}</span></td>
                  <td className="py-3.5 text-foreground/75 hidden sm:table-cell">
                    <button data-testid={`crm-row-transfer-${l.id}`} onClick={(e) => { e.stopPropagation(); setTransferLead(l); }}
                      className="inline-flex items-center gap-1 hover:text-primary transition-colors" title="Transferir para outro vendedor">
                      {l.vendedor || '—'} <UserCheck className="w-3 h-3 opacity-50" />
                    </button>
                  </td>
                  <td className="py-3.5" onClick={(e) => e.stopPropagation()}>
                    <select data-testid={`lead-stage-select-${l.id}`} value={l.etapa === 'Fechado' ? 'Perdido' : l.etapa} onChange={(e) => moveLead(l.id, e.target.value)} className="bg-muted/50 border border-border rounded-md px-2 py-1 text-xs outline-none focus:border-primary cursor-pointer">
                      {leadStages.map((s) => <option key={s} value={s} className="bg-popover">{s}</option>)}
                    </select>
                  </td>
                  <td className="py-3.5"><div className="flex items-center justify-end gap-1.5">{tempIcon[l.temperatura]}<span className="text-xs text-foreground/65">{l.temperatura}</span></div></td>
                  <td className="py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                    {canDelete && <button data-testid={`crm-delete-row-${l.id}`} title="Excluir lead" onClick={() => deleteLead(l)} className="text-muted-foreground hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ArchivedLeads open={archOpen} onClose={() => setArchOpen(false)} onOpenLead={setDetailId} onChanged={load} />

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-lg">
          <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Novo Lead</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Nome do cliente *</label><input data-testid="crm-nl-nome" className={inputCls} value={nForm.nome} onChange={(e) => setNForm({ ...nForm, nome: e.target.value })} /></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Telefone</label><input data-testid="crm-nl-telefone" className={inputCls} value={nForm.telefone} onChange={(e) => setNForm({ ...nForm, telefone: e.target.value })} placeholder="781-555-0000" /></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">E-mail</label><input data-testid="crm-nl-email" className={inputCls} value={nForm.email} onChange={(e) => setNForm({ ...nForm, email: e.target.value })} /></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Fonte</label><select data-testid="crm-nl-origem" className={inputCls} value={nForm.origem} onChange={(e) => setNForm({ ...nForm, origem: e.target.value })}>{LEAD_ORIGENS.map((o) => <option key={o} className="bg-popover">{o}</option>)}</select></div>
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Temperatura</label><select data-testid="crm-nl-temp" className={inputCls} value={nForm.temperatura} onChange={(e) => setNForm({ ...nForm, temperatura: e.target.value })}>{['Quente', 'Morno', 'Frio'].map((t) => <option key={t} className="bg-popover">{t}</option>)}</select></div>
            <div className="col-span-2"><label className="text-xs text-muted-foreground mb-1.5 block">Veículo de interesse</label><input data-testid="crm-nl-interesse" className={inputCls} value={nForm.interesse} onChange={(e) => setNForm({ ...nForm, interesse: e.target.value })} placeholder="Ex: 2019 Honda Accord" /></div>
            <div className="col-span-2">
              <label className="text-xs text-muted-foreground mb-1.5 block">Vendedor</label>
              {isSalesperson ? (
                <input className={`${inputCls} opacity-70`} value={user?.name || ''} disabled data-testid="crm-nl-vendedor-fixed" />
              ) : (
                <select data-testid="crm-nl-vendedor" className={inputCls} value={nForm.vendedor} onChange={(e) => setNForm({ ...nForm, vendedor: e.target.value })}>
                  <option value="" className="bg-popover">Sem vendedor (atribuir depois)</option>
                  {team.map((t) => <option key={t.user_id} value={t.name} className="bg-popover">{t.name}</option>)}
                </select>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setNewOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
            <button data-testid="crm-nl-save" onClick={createNewLead} disabled={nSaving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{nSaving ? 'Salvando...' : 'Criar Lead'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ContractSignedDialog lead={csLead} open={!!csLead} onClose={() => setCsLead(null)} onDone={load} />
      <LostDialog lead={lostLead} open={!!lostLead} onClose={() => setLostLead(null)} onDone={load} />
      <TransferDialog lead={transferLead} open={!!transferLead} onClose={() => setTransferLead(null)} onDone={load} />
      <LeadWorkspace leadId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} onMoveStage={moveLead} />
      <div className="ent-ribbon" data-testid="crm-summary">
        {(() => {
          const abertos = leads.filter((l) => !NO_SLA_STAGES.includes(l.etapa));
          const idles = abertos.map(idleInfo).filter(Boolean);
          const atraso = idles.filter((i) => i.overdue).length;
          const avg = idles.length ? Math.round(idles.reduce((s, i) => s + i.mins, 0) / idles.length) : 0;
          return [
            ['Leads no pipeline', leads.length, ''],
            ['Valor em oportunidades', formatCurrency(leads.reduce((s, l) => s + (l.lead_value || l.vehicle_price || 0), 0)), ''],
            ['Em atraso', atraso, atraso > 0 ? 't-bad' : ''],
            ['Em andamento', abertos.length, ''],
            ['Tempo médio de resposta', idles.length ? fmtIdle(avg) : '—', ''],
          ].map(([l, v, cls]) => (
            <div key={l}><p className="ent-label whitespace-nowrap">{l}</p><p className={`ent-value mt-1 ${cls}`}>{v}</p></div>
          ));
        })()}
      </div>
    </div>
  );
};

export default Crm;
